import { Moon, Sun } from 'lucide-react'
import { cn } from '../lib/utils'
import { useTheme } from './theme-provider'
import { Button } from './ui/button'
import { SidebarMenuButton } from './ui/sidebar'

const themeCycle: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system']
const themeLabels: Record<(typeof themeCycle)[number], string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
}

interface ThemeToggleProps {
  className?: string
  variant?: 'icon' | 'sidebar'
}

export function ThemeToggle({ className, variant = 'icon' }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme } = useTheme()

  const nextTheme = themeCycle[(themeCycle.indexOf(theme) + 1) % themeCycle.length]
  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }
  const icon =
    resolvedTheme === 'light' ? (
      <Sun className="h-[1.2rem] w-[1.2rem]" />
    ) : (
      <Moon className="h-[1.2rem] w-[1.2rem]" />
    )

  if (variant === 'sidebar') {
    return (
      <SidebarMenuButton
        tooltip="Theme"
        onClick={toggleTheme}
        className={cn(
          'cursor-pointer rounded-lg border border-transparent hover:border-sidebar-border/70',
          className
        )}
        aria-label={`Switch theme. Current theme: ${themeLabels[theme]}`}
        title={`Theme: ${themeLabels[theme]}`}
      >
        {icon}
        <span>Theme</span>
        <span className="ml-auto text-xs text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden">
          {themeLabels[theme]}
        </span>
      </SidebarMenuButton>
    )
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setTheme(nextTheme)}
      className={cn('w-9 cursor-pointer px-0', className)}
      aria-label={`Switch theme. Current theme: ${theme}`}
      title={`Theme: ${theme}`}
    >
      {icon}
      <span className="sr-only">Toggle theme</span>
    </Button>
  )
}
